import type { KnowledgeItem } from "@/mocks/knowledge";
import { knowledgeItems as seededItems } from "@/mocks/knowledge";

const LS_KEY = "scienceone.knowledgeItems.v1";
const LS_DELETED_KEY = "scienceone.knowledgeItems.deleted.v1";

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function loadDeletedIds(): Set<string> {
  const stored = safeParse<string[]>(localStorage.getItem(LS_DELETED_KEY));
  if (!stored || !Array.isArray(stored)) return new Set();
  return new Set(stored.filter(Boolean));
}

function saveDeletedIds(ids: Set<string>) {
  localStorage.setItem(LS_DELETED_KEY, JSON.stringify(Array.from(ids)));
}

export function loadKnowledgeItems(): KnowledgeItem[] {
  const deleted = loadDeletedIds();
  const stored = safeParse<KnowledgeItem[]>(localStorage.getItem(LS_KEY));
  if (!stored || !Array.isArray(stored)) return seededItems.filter((it) => !deleted.has(it.id));

  // Merge: stored overrides seeded by id, keep seeded then prepend new.
  const byId = new Map<string, KnowledgeItem>();
  for (const it of seededItems) byId.set(it.id, it);
  for (const it of stored) byId.set(it.id, it);

  const seededIds = new Set(seededItems.map((it) => it.id));
  const merged: KnowledgeItem[] = [];
  for (const it of seededItems) {
    if (deleted.has(it.id)) continue;
    merged.push(byId.get(it.id)!);
  }
  for (const it of stored) {
    if (deleted.has(it.id)) continue;
    if (!seededIds.has(it.id)) merged.unshift(it);
  }
  return merged;
}

export function saveKnowledgeItems(list: KnowledgeItem[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(list));
}

export function addKnowledgeItems(items: KnowledgeItem[]) {
  const prev = loadKnowledgeItems();
  const next = [...items, ...prev];
  saveKnowledgeItems(next);
  return next;
}

export function getKnowledgeItemById(itemId: string): KnowledgeItem | null {
  return loadKnowledgeItems().find((it) => it.id === itemId) ?? null;
}

export function removeKnowledgeItemById(itemId: string) {
  // soft-delete for seeded items and persisted items
  const deleted = loadDeletedIds();
  deleted.add(itemId);
  saveDeletedIds(deleted);

  const stored = safeParse<KnowledgeItem[]>(localStorage.getItem(LS_KEY)) || [];
  const nextStored = stored.filter((it) => it.id !== itemId);
  localStorage.setItem(LS_KEY, JSON.stringify(nextStored));
}

export function getKnowledgeItemsByKb(kbId: string) {
  return loadKnowledgeItems().filter((it) => it.kbId === kbId);
}

